import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Sprout, Droplets, ArrowRight } from "lucide-react";

const round = (v, d = 3) => Math.round(v * 10 ** d) / 10 ** d;
const FLOZ_PER_GAL = 128;

// ---- Granular: exact app math (lb/1,000 sqft or kg/100 m²) ----
function calculateNPK({ area, areaUnit, nPct, pPct, kPct, targetNutrient = "N", targetRate, rateUnit = "lb_per_1000sqft", bagSize, bagPrice }) {
  if (!area || area <= 0) throw new Error("Area must be greater than 0");
  if (!targetRate || targetRate <= 0) throw new Error("Target rate must be greater than 0");
  const nutrientPct = { N: nPct, P: pPct, K: kPct }[targetNutrient];
  if (!nutrientPct || nutrientPct <= 0) throw new Error(`${targetNutrient} percentage must be greater than 0`);
  const SQFT_PER_M2 = 10.7639;
  let fertilizerAmount, fertilizerUnit, coverageArea, coverageUnit, fert;
  if (rateUnit === "lb_per_1000sqft") {
    const areaSqft = areaUnit === "sqft" ? area : area * SQFT_PER_M2;
    fert = (targetRate * areaSqft) / 1000 / (nutrientPct / 100);
    fertilizerAmount = round(fert); fertilizerUnit = "lb"; coverageArea = round(areaSqft, 2); coverageUnit = "sq ft";
  } else {
    const areaM2 = areaUnit === "m2" ? area : area / SQFT_PER_M2;
    fert = (targetRate * areaM2) / 100 / (nutrientPct / 100);
    fertilizerAmount = round(fert); fertilizerUnit = "kg"; coverageArea = round(areaM2, 2); coverageUnit = "m²";
  }
  let bagsNeeded = null, estimatedCost = null;
  if (bagSize && bagSize > 0) { bagsNeeded = round(fertilizerAmount / bagSize, 2); if (bagPrice && bagPrice > 0) estimatedCost = round(bagsNeeded * bagPrice, 2); }
  // Application rate & delivered nutrients are PER reference area (1,000 sq ft or 100 m²), not whole-lawn totals.
  const appRatePerArea = targetRate / (nutrientPct / 100);
  return {
    fertilizerAmount, fertilizerUnit,
    appRatePerArea: round(appRatePerArea, 3),
    nutrientDelivered: {
      N: round((nPct / 100) * appRatePerArea),
      P: round((pPct / 100) * appRatePerArea),
      K: round((kPct / 100) * appRatePerArea),
    },
    bagsNeeded, estimatedCost, coverageArea, coverageUnit,
  };
}

// ---- Liquid: exact app math (concentrate + dilution), imperial or metric ----
function calculateLiquid({ area, nPct, pPct, kPct, targetNutrient = "N", targetRate, density, appRate, metric = false }) {
  if (!area || area <= 0) throw new Error("Lawn area must be greater than 0");
  if (!targetRate || targetRate <= 0) throw new Error("Target rate must be greater than 0");
  const pct = { N: nPct, P: pPct, K: kPct }[targetNutrient];
  if (!pct || pct <= 0) throw new Error(`${targetNutrient} percentage must be greater than 0`);
  if (!density || density <= 0) throw new Error("Density must be greater than 0");
  if (!appRate || appRate <= 0) throw new Error("Application rate must be greater than 0");

  // Imperial: gal→fl oz (128), rate per 1,000 sq ft. Metric: L→mL (1000), rate per 100 m².
  const SUB = metric ? 1000 : 128; // small units per big volume unit
  const AREA_BASE = metric ? 100 : 1000; // reference area

  // Metric density is entered in kg/m³ → convert to kg/L for the math.
  const densityEff = metric ? density / 1000 : density;
  const nutrientPerBigVol = densityEff * (pct / 100); // (kg or lb) of nutrient per (L or gal) concentrate
  const concSmallPerArea = (targetRate / nutrientPerBigVol) * SUB; // fl oz/1k  OR  mL/100 m²
  const dilution = concSmallPerArea / appRate; // fl oz per gal  OR  mL per L
  const mixedBigTotal = (appRate * area) / AREA_BASE; // gal OR L
  const totalConcSmall = (concSmallPerArea * area) / AREA_BASE; // fl oz OR mL
  const concBigPerArea = concSmallPerArea / SUB; // gal/1k OR L/100 m²
  const deliver = (p) => round(concBigPerArea * densityEff * (p / 100), 3);
  const nDel = deliver(nPct), pDel = deliver(pPct), kDel = deliver(kPct);
  const targetDel = { N: nDel, P: pDel, K: kDel }[targetNutrient];
  return {
    concentratePer1000: round(concSmallPerArea, metric ? 1 : 2),
    dilutionOzPerGal: round(dilution, metric ? 1 : 2),
    mixedGalTotal: round(mixedBigTotal, 2),
    totalConcOz: round(totalConcSmall, metric ? 0 : 1),
    totalConcGal: round(totalConcSmall / SUB, 2),
    delivered: { N: nDel, P: pDel, K: kDel },
    pctOfTarget: round((targetDel / targetRate) * 100, 0),
    coverageArea: round(area, 0),
  };
}

const inputCls =
  "w-full rounded-xl border border-[var(--ld-line)] bg-[var(--ld-bg)] px-3.5 py-2.5 text-sm font-bold text-[var(--ld-text)] outline-none placeholder:text-[var(--ld-muted)] focus:border-[var(--ld-lime)]";

const Field = ({ label, unit, children }) => (
  <label className="block">
    <span className="mb-1.5 flex min-h-[2.6em] flex-col justify-start text-[11px] font-black uppercase tracking-wider leading-tight text-[var(--ld-muted)]">
      <span>{label}</span>
      {unit && <span className="text-[var(--ld-muted)]/70">{unit}</span>}
    </span>
    {children}
  </label>
);

const NutrientGrid = ({ nPct, pPct, kPct, setN, setP, setK, defs }) => (
  <div className="grid grid-cols-3 gap-3">
    {[["N", nPct, setN, defs.N], ["P", pPct, setP, defs.P], ["K", kPct, setK, defs.K]].map(([k, v, set, ph]) => (
      <div key={k} className="relative">
        <input type="number" value={v} placeholder={String(ph)} onChange={(e) => set(e.target.value)} className={inputCls} data-testid={`npk-${k.toLowerCase()}`} />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-black text-[var(--ld-lime)]">{k}%</span>
      </div>
    ))}
  </div>
);

const TargetPicker = ({ targetNutrient, setTargetNutrient }) => (
  <div className="flex gap-2">
    {["N", "P", "K"].map((nk) => (
      <button key={nk} onClick={() => setTargetNutrient(nk)} data-testid={`npk-target-${nk}`}
        className={`flex-1 rounded-xl py-2.5 text-sm font-black transition-colors ${targetNutrient === nk ? "bg-[var(--ld-lime)] text-[var(--ld-bg)]" : "bg-[var(--ld-bg)] text-[var(--ld-muted)] hover:text-[var(--ld-text)]"}`}>
        {nk}
      </button>
    ))}
  </div>
);

const Delivered = ({ delivered, unit, target, targetNutrient }) => (
  <div className="mt-5 border-t border-[var(--ld-line)] pt-4">
    <div className="mb-2 text-center text-[10px] font-black uppercase tracking-wider text-[var(--ld-muted)]">
      Nutrients delivered · {unit}
    </div>
    <div className="grid grid-cols-3 gap-2 text-center">
      {["N", "P", "K"].map((nk) => (
        <div key={nk}>
          <div className="ld-display text-xl text-[var(--ld-text)]" data-testid={`npk-delivered-${nk.toLowerCase()}`}>{delivered[nk]}</div>
          <div className="text-[10px] font-black uppercase tracking-wider text-[var(--ld-muted)]">
            {nk}
            {nk === targetNutrient && target ? <span className="block text-[var(--ld-lime)]">100% of target</span> : null}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const DEFAULTS = { area: 5000, nPct: 5, pPct: 4, kPct: 0, targetRate: 0.75, bagSize: 32, bagPrice: 18, density: 9.07, appRate: 1 };
const rv = (v, d) => (v === "" || v == null ? d : v);

export default function NPKCalculator() {
  const [form, setForm] = useState("granular");
  const [metric, setMetric] = useState(false);
  const [area, setArea] = useState("");
  const [nPct, setNPct] = useState("");
  const [pPct, setPPct] = useState("");
  const [kPct, setKPct] = useState("");
  const [targetNutrient, setTargetNutrient] = useState("N");
  const [targetRate, setTargetRate] = useState("");
  const [bagSize, setBagSize] = useState("");
  const [bagPrice, setBagPrice] = useState("");
  // liquid-only
  const [density, setDensity] = useState("");
  const [appRate, setAppRate] = useState("");

  const areaUnit = metric ? "m2" : "sqft";
  const rateUnit = metric ? "kg_per_100m2" : "lb_per_1000sqft";

  const granular = useMemo(() => {
    try { return calculateNPK({ area: +rv(area, DEFAULTS.area), areaUnit, nPct: +rv(nPct, DEFAULTS.nPct), pPct: +rv(pPct, DEFAULTS.pPct), kPct: +rv(kPct, DEFAULTS.kPct), targetNutrient, targetRate: +rv(targetRate, DEFAULTS.targetRate), rateUnit, bagSize: +bagSize || undefined, bagPrice: +bagPrice || undefined }); }
    catch (e) { return { error: e.message }; }
  }, [area, areaUnit, nPct, pPct, kPct, targetNutrient, targetRate, rateUnit, bagSize, bagPrice]);

  const liquid = useMemo(() => {
    try { return calculateLiquid({ area: +rv(area, DEFAULTS.area), nPct: +rv(nPct, DEFAULTS.nPct), pPct: +rv(pPct, DEFAULTS.pPct), kPct: +rv(kPct, DEFAULTS.kPct), targetNutrient, targetRate: +rv(targetRate, DEFAULTS.targetRate), density: +rv(density, DEFAULTS.density), appRate: +rv(appRate, DEFAULTS.appRate), metric }); }
    catch (e) { return { error: e.message }; }
  }, [area, nPct, pPct, kPct, targetNutrient, targetRate, density, appRate, metric]);

  const isLiquid = form === "liquid";
  // Liquid unit labels
  const LU = metric
    ? { area: "m²", target: "kg / 100 m²", density: "kg / m³", app: "L / 100 m²", conc: "mL / 100 m²", dil: "mL/L", big: "L", small: "mL", del: "kg/100 m²" }
    : { area: "sq ft", target: "lb / 1,000 sq ft", density: "lb / gal", app: "gal / 1,000 sq ft", conc: "fl oz / 1k", dil: "oz/gal", big: "gal", small: "oz", del: "lb/1k" };

  return (
    <div data-testid="npk-calculator" className="grid gap-6 lg:grid-cols-[1fr_360px]">
      {/* Inputs */}
      <div className="rounded-3xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/60 p-6 sm:p-7">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-full bg-[var(--ld-bg)] p-1">
            {[{ id: "granular", label: "Granular", icon: Sprout }, { id: "liquid", label: "Liquid", icon: Droplets }].map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setForm(t.id)} data-testid={`npk-type-${t.id}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-black transition-colors ${form === t.id ? "bg-[var(--ld-lime)] text-[var(--ld-bg)]" : "text-[var(--ld-muted)] hover:text-[var(--ld-text)]"}`}>
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </div>
          {!isLiquid ? (
            <button onClick={() => setMetric((m) => !m)} data-testid="npk-unit-toggle"
              className="rounded-full border border-[var(--ld-line)] px-4 py-2 text-xs font-black text-[var(--ld-text)] transition-colors hover:border-[var(--ld-lime)]/50">
              {metric ? "Metric (kg / 100 m²)" : "Imperial (lb / 1,000 sq ft)"}
            </button>
          ) : (
            <button onClick={() => setMetric((m) => !m)} data-testid="npk-unit-toggle"
              className="rounded-full border border-[var(--ld-line)] px-4 py-2 text-xs font-black text-[var(--ld-text)] transition-colors hover:border-[var(--ld-lime)]/50">
              {metric ? "Metric (mL / L)" : "Imperial (fl oz / gal)"}
            </button>
          )}
        </div>

        {/* Shared: area + target nutrient */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Lawn area" unit={`(${isLiquid ? LU.area : metric ? "m²" : "sq ft"})`}>
            <input type="number" value={area} placeholder={String(DEFAULTS.area)} onChange={(e) => setArea(e.target.value)} className={inputCls} data-testid="npk-area" />
          </Field>
          <Field label="Target nutrient">
            <TargetPicker targetNutrient={targetNutrient} setTargetNutrient={setTargetNutrient} />
          </Field>
        </div>

        {/* Product analysis */}
        <div className="mt-4">
          <span className="mb-1.5 block text-[11px] font-black uppercase tracking-wider text-[var(--ld-muted)]">
            {isLiquid ? "Liquid analysis (from the bottle)" : "Product analysis (from the label)"}
          </span>
          <NutrientGrid nPct={nPct} pPct={pPct} kPct={kPct} setN={setNPct} setP={setPPct} setK={setKPct} defs={{ N: DEFAULTS.nPct, P: DEFAULTS.pPct, K: DEFAULTS.kPct }} />
        </div>

        {isLiquid ? (
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Field label={`Target ${targetNutrient} rate`} unit={`(${LU.target})`}>
              <input type="number" step="0.05" value={targetRate} placeholder={String(DEFAULTS.targetRate)} onChange={(e) => setTargetRate(e.target.value)} className={inputCls} data-testid="npk-rate" />
            </Field>
            <Field label="Density" unit={`(${LU.density})`}>
              <input type="number" step="0.01" value={density} placeholder={String(DEFAULTS.density)} onChange={(e) => setDensity(e.target.value)} className={inputCls} data-testid="npk-density" />
            </Field>
            <Field label="Application rate" unit={`(${LU.app})`}>
              <input type="number" step="0.1" value={appRate} placeholder={String(DEFAULTS.appRate)} onChange={(e) => setAppRate(e.target.value)} className={inputCls} data-testid="npk-apprate" />
            </Field>
          </div>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Field label="Target rate" unit={`(${metric ? "kg/100 m²" : "lb/1,000 sq ft"})`}>
              <input type="number" step="0.05" value={targetRate} placeholder={String(DEFAULTS.targetRate)} onChange={(e) => setTargetRate(e.target.value)} className={inputCls} data-testid="npk-rate" />
            </Field>
            <Field label="Bag size" unit={`(${metric ? "kg" : "lb"}) · optional`}>
              <input type="number" value={bagSize} placeholder={String(DEFAULTS.bagSize)} onChange={(e) => setBagSize(e.target.value)} className={inputCls} data-testid="npk-bag-size" />
            </Field>
            <Field label="Bag price ($)" unit="· optional">
              <input type="number" value={bagPrice} placeholder={String(DEFAULTS.bagPrice)} onChange={(e) => setBagPrice(e.target.value)} className={inputCls} data-testid="npk-bag-price" />
            </Field>
          </div>
        )}
      </div>

      {/* Result */}
      <motion.div layout className="flex flex-col justify-center rounded-3xl border border-[var(--ld-lime)]/25 bg-gradient-to-b from-[var(--ld-surface)] to-[var(--ld-bg)] p-6 sm:p-7" data-testid="npk-result">
        {isLiquid ? (
          liquid.error ? (
            <p className="text-sm font-bold text-red-400" data-testid="npk-error">{liquid.error}</p>
          ) : (
            <>
              <span className="text-[11px] font-black uppercase tracking-[0.22em] text-[var(--ld-lime)]">Concentrate</span>
              <div className="mt-2 flex items-end gap-2">
                <span className="ld-display text-6xl text-[var(--ld-lime-bright)]" data-testid="npk-amount">{liquid.concentratePer1000}</span>
                <span className="mb-2 text-sm font-black text-[var(--ld-text)]">{LU.conc}</span>
              </div>
              <p className="mt-1 text-sm font-semibold text-[var(--ld-muted)]">
                Dilution rate <span className="font-black text-[var(--ld-text)]">{liquid.dilutionOzPerGal} {LU.dil}</span>.
                Mix <span className="font-black text-[var(--ld-text)]">{liquid.mixedGalTotal} {LU.big}</span> total for {liquid.coverageArea.toLocaleString()} {LU.area}.
              </p>
              <div className="mt-3 rounded-xl bg-[var(--ld-lime)]/12 px-4 py-3 text-sm font-black text-[var(--ld-lime-bright)]" data-testid="npk-total">
                Total concentrate: {liquid.totalConcOz} {LU.small} ({liquid.totalConcGal} {LU.big})
              </div>
              <Delivered delivered={liquid.delivered} unit={LU.del} />
              <p className="mt-2 text-center text-xs font-black uppercase tracking-wider text-[var(--ld-lime)]">{liquid.pctOfTarget}% of target</p>
            </>
          )
        ) : granular.error ? (
          <p className="text-sm font-bold text-red-400" data-testid="npk-error">{granular.error}</p>
        ) : (
          <>
            <span className="text-[11px] font-black uppercase tracking-[0.22em] text-[var(--ld-lime)]">You'll need</span>
            <div className="mt-2 flex items-end gap-2">
              <span className="ld-display text-6xl text-[var(--ld-lime-bright)]" data-testid="npk-amount">{granular.fertilizerAmount}</span>
              <span className="mb-2 text-lg font-black text-[var(--ld-text)]">{granular.fertilizerUnit}</span>
            </div>
            <p className="mt-1 text-sm font-semibold text-[var(--ld-muted)]">
              at <span className="font-black text-[var(--ld-text)]">{granular.appRatePerArea} {metric ? "kg/100 m²" : "lb/1,000 sq ft"}</span> to cover {granular.coverageArea.toLocaleString()} {granular.coverageUnit}.
            </p>
            <Delivered delivered={granular.nutrientDelivered} unit={metric ? "kg / 100 m²" : "lb / 1,000 sq ft"} target targetNutrient={targetNutrient} />
            {granular.bagsNeeded != null && (
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-[var(--ld-lime)]/12 px-4 py-3 text-sm font-black text-[var(--ld-lime-bright)]">
                <ArrowRight className="h-4 w-4" />
                {granular.bagsNeeded} bag{granular.bagsNeeded === 1 ? "" : "s"}
                {granular.estimatedCost != null && ` · about $${granular.estimatedCost}`}
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
